// API base resolution, timeouts, and multi-base fallback live in the shared
// helper (src/utils/apiBase.js). This service used to carry its own copy
// that unconditionally retried localhost:5001 — including on 401s — which
// blasted the console with blocked mixed-content requests whenever a
// session expired in production.
import { fetchWithApiFallback } from '../utils/apiBase';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

// Helper for GET requests
const apiGet = async (endpoint) => {
  const response = await fetchWithApiFallback(endpoint, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    const err = new Error(`API error: ${response.status}`);
    err.statusCode = response.status;
    throw err;
  }
  return response.json();
};

// Helper for POST requests
const apiPost = async (endpoint, data) => {
  const response = await fetchWithApiFallback(endpoint, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    // Surface the backend's friendly error message (e.g. validation errors)
    // when present, instead of a generic "API error: 400". Falls back to the
    // status code for non-JSON bodies. `err.data` carries the full body for
    // callers that need structured fields.
    let body = null;
    try { body = await response.json(); } catch { /* non-JSON error body */ }
    const err = new Error(body?.error || body?.message || `API error: ${response.status}`);
    err.statusCode = response.status;
    err.data = body;
    throw err;
  }
  return response.json();
};

const subscriptionService = {
  // Get pricing plans for a product
  getPricingPlans: async (product) => {
    return apiGet(`/subscriptions/plans/${product}`);
  },

  // Start a free trial
  startTrial: async (product, plan = 'professional') => {
    return apiPost('/subscriptions/trial/start', { product, plan });
  },

  // Get subscription status for a product
  getSubscriptionStatus: async (product) => {
    return apiGet(`/subscriptions/status/${product}`);
  },

  // Lightweight eligibility check. Does not wait on Stripe reconciliation.
  getWelcomeOffer: async (product) => {
    return apiGet(`/subscriptions/welcome-offer/${product}`);
  },

  // Get canonical current subscription record for a product
  getCurrentSubscription: async (product) => {
    const suffix = product ? `?product=${encodeURIComponent(product)}` : '';
    return apiGet(`/subscriptions/current${suffix}`);
  },

  // Get all subscriptions for current user
  getAllSubscriptions: async () => {
    return apiGet('/subscriptions/all');
  },

  // Create checkout session for paid subscription
  createCheckoutSession: async (product, plan, billingCycle = 'monthly') => {
    return apiPost('/subscriptions/checkout', { product, plan, billingCycle });
  },

  // Create setup intent for collecting payment method
  createSetupIntent: async (product) => {
    return apiPost('/subscriptions/setup-intent', { product });
  },

  // Save payment method
  savePaymentMethod: async (product, paymentMethodId) => {
    return apiPost('/subscriptions/payment-method', { product, paymentMethodId });
  },

  // Cancel subscription
  cancelSubscription: async (product, cancelImmediately = false) => {
    return apiPost(`/subscriptions/cancel/${product}`, { cancelImmediately });
  },

  // Change billing cycle (monthly <-> yearly) on an active paid subscription.
  // monthly -> yearly charges the prorated difference immediately; yearly ->
  // monthly credits the unused year toward upcoming invoices.
  changeBillingCycle: async (product, billingCycle) => {
    return apiPost('/subscriptions/billing-cycle', { product, billingCycle });
  },

  // Helper to check if user has active subscription
  hasActiveSubscription: async (product) => {
    try {
      const status = await subscriptionService.getSubscriptionStatus(product);
      return status.isActive;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  },

  // Helper to get trial days remaining
  getTrialDaysRemaining: async (product) => {
    try {
      const status = await subscriptionService.getSubscriptionStatus(product);
      return status.daysRemaining || 0;
    } catch (error) {
      console.error('Error getting trial days:', error);
      return 0;
    }
  },

  // Get usage and limits for a product
  getUsageAndLimits: async (product) => {
    return apiGet(`/subscriptions/usage/${product}`);
  },
};

export default subscriptionService;
